// ────────────────────────────────────────────────────────────────────────────
// Slide Studio – HTML Exporter
//
// Converts a Slide (from the editor store) into a self-contained HTML string
// that mirrors the style of the existing presentation templates (Tailwind
// utility classes + inline styles, Google Fonts links, 1920×1080 canvas).
// ────────────────────────────────────────────────────────────────────────────

import type {
  Slide,
  SlideElement,
  TextElement,
  ImageElement,
  ShapeElement,
  ContainerElement,
  GroupElement,
  TextStyle,
  BorderStyle,
} from './types'
import { isTextElement } from './types'

import { contentToHtml, containsMarkdown } from './markdownUtils'

// Re-export collectGoogleFonts so the PDF exporter can use it
export { collectGoogleFonts, buildGoogleFontsLink }

// ─── Font helpers ───────────────────────────────────────────────────────────

/** Extract the human-readable font name from a CSS font-family value */
function extractFontName(fontFamily: string): string {
  return fontFamily
    .split(',')[0]
    .trim()
    .replace(/^['"]|['"]$/g, '')
}

/** Collect every unique Google-Fonts-compatible family used in the slide */
function collectGoogleFonts(slide: Slide): string[] {
  const families = new Set<string>()
  for (const el of slide.elements) {
    if (isTextElement(el)) {
      const name = extractFontName((el as TextElement).textStyle.fontFamily)
      // Skip generic / system fonts
      if (!['Arial', 'Verdana', 'Georgia', 'Courier New', 'Times New Roman'].includes(name)) {
        families.add(name)
      }
    }
  }
  return Array.from(families)
}

function buildGoogleFontsLink(families: string[]): string {
  if (families.length === 0) return ''
  const params = families
    .map((f) => `family=${f.replace(/ /g, '+')}:wght@100;200;300;400;500;600;700;800;900`)
    .join('&')
  return [
    `<link rel="preconnect" href="https://fonts.googleapis.com" />`,
    `<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />`,
    `<link href="https://fonts.googleapis.com/css2?${params}&display=swap" rel="stylesheet" />`,
  ].join('\n  ')
}

// ─── Style builders ─────────────────────────────────────────────────────────

function buildTextInlineStyle(ts: TextStyle, isBlockContent = false): string {
  const parts: string[] = []
  parts.push(`font-family: ${ts.fontFamily}`)
  parts.push(`font-size: ${ts.fontSize}px`)
  parts.push(`font-weight: ${ts.fontWeight}`)
  if (ts.fontStyle !== 'normal') parts.push(`font-style: ${ts.fontStyle}`)
  if (ts.textDecoration !== 'none') parts.push(`text-decoration: ${ts.textDecoration}`)
  parts.push(`line-height: ${ts.lineHeight}`)
  if (ts.letterSpacing !== 0) parts.push(`letter-spacing: ${ts.letterSpacing}px`)
  if (ts.textTransform !== 'none') parts.push(`text-transform: ${ts.textTransform}`)
  parts.push(`text-align: ${ts.textAlign}`)
  parts.push(`color: ${ts.color}`)
  // Match browser renderer: word-break and white-space
  // When content is Markdown or legacy HTML (block elements like <p>, <ul>),
  // use 'normal' so block-level elements control spacing; otherwise 'pre-wrap'.
  parts.push('word-break: break-word')
  parts.push(`white-space: ${isBlockContent ? 'normal' : 'pre-wrap'}`)
  return parts.join('; ')
}

function buildBorderStyle(border: BorderStyle): string {
  const parts: string[] = []
  if (border.width > 0) {
    parts.push(`border: ${border.width}px solid ${border.color}`)
  }
  if (border.radius > 0) {
    parts.push(`border-radius: ${border.radius}px`)
  }
  return parts.join('; ')
}

function buildBoxPositionStyle(el: SlideElement): string {
  const parts: string[] = [
    'position: absolute',
    `left: ${el.rect.x}px`,
    `top: ${el.rect.y}px`,
    `width: ${el.rect.width}px`,
    `height: ${el.rect.height}px`,
  ]

  if (el.rotation !== 0) {
    parts.push(`transform: rotate(${el.rotation}deg)`)
  }

  if (el.opacity < 1) {
    parts.push(`opacity: ${el.opacity}`)
  }

  return parts.join('; ')
}

// ─── Element renderers ──────────────────────────────────────────────────────

function renderTextElement(el: TextElement): string {
  const posStyle = buildBoxPositionStyle(el)
  const trimmedContent = el.content?.trim() ?? ''
  const isBlock = trimmedContent.startsWith('<') || containsMarkdown(trimmedContent) || (el.content || '').includes('\n')
  const textStyle = buildTextInlineStyle(el.textStyle, isBlock)
  const borderStyle = buildBorderStyle(el.border)

  const paddingStyle = `padding: ${el.padding.top}px ${el.padding.right}px ${el.padding.bottom}px ${el.padding.left}px`
  const bgStyle = el.backgroundColor !== 'transparent' ? `background-color: ${el.backgroundColor}` : ''

  // Vertical alignment via flexbox
  const vAlignMap: Record<string, string> = {
    top: 'flex-start',
    middle: 'center',
    bottom: 'flex-end',
  }
  const vAlign = `display: flex; flex-direction: column; justify-content: ${vAlignMap[el.textStyle.verticalAlign] ?? 'flex-start'}`

  const styleParts = [posStyle, textStyle, borderStyle, paddingStyle, bgStyle, vAlign, 'overflow: hidden']
    .filter(Boolean)
    .join('; ')

  const markdownHtml = contentToHtml(el.content)

  return `  <div style="${styleParts}">${markdownHtml}</div>`
}

function renderImageElement(el: ImageElement): string {
  const posStyle = buildBoxPositionStyle(el)
  const borderStyle = buildBorderStyle(el.border)
  const bgStyle = el.backgroundColor !== 'transparent' ? `background-color: ${el.backgroundColor}` : ''

  const containerParts = [posStyle, borderStyle, bgStyle, 'overflow: hidden'].filter(Boolean).join('; ')

  if (!el.src) {
    // No image source — don't render anything (no placeholder box)
    return ''
  }

  const altAttr = el.alt ? ` alt="${escapeAttr(el.alt)}"` : ' alt=""'
  const crop = el.cropRegion

  if (crop) {
    // Cropped: use a wrapper with overflow:hidden and scale/position the image
    // so only the crop region is visible, stretched to fill the element bounds.
    const imgW = `${(1 / crop.width) * 100}%`
    const imgH = `${(1 / crop.height) * 100}%`
    const imgLeft = `${(-crop.x / crop.width) * 100}%`
    const imgTop = `${(-crop.y / crop.height) * 100}%`
    const imgStyle = `position: absolute; width: ${imgW}; height: ${imgH}; left: ${imgLeft}; top: ${imgTop}; max-width: none; object-fit: fill`
    return `  <div style="${containerParts}">
    <div style="width: 100%; height: 100%; position: relative; overflow: hidden">
      <img src="${escapeAttr(el.src)}"${altAttr} style="${imgStyle}" />
    </div>
  </div>`
  }

  const imgStyle = `width: 100%; height: 100%; object-fit: ${el.objectFit}`
  return `  <div style="${containerParts}">
    <img src="${escapeAttr(el.src)}"${altAttr} style="${imgStyle}" />
  </div>`
}

function renderShapeElement(el: ShapeElement): string {
  const posStyle = buildBoxPositionStyle(el)
  const borderStyle = buildBorderStyle(el.border)
  const fillStyle = el.fill !== 'transparent' ? `background-color: ${el.fill}` : ''

  let shadowStyle = ''
  if (el.shadow) {
    shadowStyle = `box-shadow: ${el.shadow.x}px ${el.shadow.y}px ${el.shadow.blur}px ${el.shadow.spread}px ${el.shadow.color}`
  }

  // Ellipse gets full border-radius
  let shapeRadius = ''
  if (el.shapeKind === 'ellipse') {
    shapeRadius = 'border-radius: 50%'
  }

  const styleParts = [posStyle, fillStyle, borderStyle, shadowStyle, shapeRadius]
    .filter(Boolean)
    .join('; ')

  return `  <div style="${styleParts}"></div>`
}

function renderContainerElement(el: ContainerElement, allElements: SlideElement[]): string {
  const posStyle = buildBoxPositionStyle(el)
  const borderStyle = buildBorderStyle(el.border)
  const bgStyle = el.backgroundColor !== 'transparent' ? `background-color: ${el.backgroundColor}` : ''
  const paddingStyle = `padding: ${el.padding.top}px ${el.padding.right}px ${el.padding.bottom}px ${el.padding.left}px`
  const overflowStyle = el.clipContent ? 'overflow: hidden' : ''

  let layoutStyle = ''
  if (el.layout === 'stack') {
    layoutStyle = 'position: relative'
  } else {
    const direction = el.layout === 'row' ? 'row' : 'column'
    layoutStyle = [
      'display: flex',
      `flex-direction: ${direction}`,
      `gap: ${el.gap}px`,
      `align-items: ${el.alignItems}`,
      `justify-content: ${el.justifyContent}`,
      el.wrap ? 'flex-wrap: wrap' : 'flex-wrap: nowrap',
    ].join('; ')
  }

  const styleParts = [posStyle, layoutStyle, paddingStyle, borderStyle, bgStyle, overflowStyle]
    .filter(Boolean)
    .join('; ')

  // Render children
  const childrenHtml = el.childIds
    .map((childId) => {
      const child = allElements.find((e) => e.id === childId)
      if (!child || !child.visible) return ''

      // For stack layout, children are absolutely positioned to fill the container
      if (el.layout === 'stack') {
        const childHtml = renderElement(child, allElements)
        // Wrap in a full-size absolute div
        return `    <div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%">\n  ${childHtml}\n    </div>`
      }

      // For row/column, render children with their width/height but without absolute positioning
      const childInnerHtml = renderChildElement(child, allElements)
      return childInnerHtml
    })
    .filter(Boolean)
    .join('\n')

  return `  <div style="${styleParts}">
${childrenHtml}
  </div>`
}

/** Render a child element inside a container (no absolute positioning) */
function renderChildElement(el: SlideElement, allElements: SlideElement[]): string {
  if (!el.visible) return ''

  const sizeParts: string[] = [
    `width: ${el.rect.width}px`,
    `height: ${el.rect.height}px`,
    'flex-shrink: 0',
  ]

  if (el.rotation !== 0) {
    sizeParts.push(`transform: rotate(${el.rotation}deg)`)
  }
  if (el.opacity < 1) {
    sizeParts.push(`opacity: ${el.opacity}`)
  }

  const wrapperStyle = sizeParts.join('; ')

  // For containers, render recursively but override positioning
  if (el.type === 'container') {
    const ctr = el as ContainerElement
    return renderContainerChildContainer(ctr, allElements, wrapperStyle)
  }

  // For other elements, render their inner content wrapped in a sized div
  let innerHtml = ''
  switch (el.type) {
    case 'text':
      innerHtml = renderTextElementInner(el as TextElement)
      break
    case 'markdown':
      innerHtml = renderTextElementInner(el as TextElement)
      break
    case 'image':
      innerHtml = renderImageElementInner(el as ImageElement)
      break
    case 'shape':
      innerHtml = renderShapeElementInner(el as ShapeElement)
      break
  }

  return `    <div style="${wrapperStyle}">${innerHtml}</div>`
}

/** Render a container that is a child of another container (no absolute position) */
function renderContainerChildContainer(el: ContainerElement, allElements: SlideElement[], wrapperStyle: string): string {
  const borderStyle = buildBorderStyle(el.border)
  const bgStyle = el.backgroundColor !== 'transparent' ? `background-color: ${el.backgroundColor}` : ''
  const paddingStyle = `padding: ${el.padding.top}px ${el.padding.right}px ${el.padding.bottom}px ${el.padding.left}px`
  const overflowStyle = el.clipContent ? 'overflow: hidden' : ''

  let layoutStyle = ''
  if (el.layout === 'stack') {
    layoutStyle = 'position: relative'
  } else {
    const direction = el.layout === 'row' ? 'row' : 'column'
    layoutStyle = [
      'display: flex',
      `flex-direction: ${direction}`,
      `gap: ${el.gap}px`,
      `align-items: ${el.alignItems}`,
      `justify-content: ${el.justifyContent}`,
      el.wrap ? 'flex-wrap: wrap' : 'flex-wrap: nowrap',
    ].join('; ')
  }

  const innerStyleParts = [wrapperStyle, layoutStyle, paddingStyle, borderStyle, bgStyle, overflowStyle]
    .filter(Boolean)
    .join('; ')

  const childrenHtml = el.childIds
    .map((childId) => {
      const child = allElements.find((e) => e.id === childId)
      if (!child || !child.visible) return ''
      if (el.layout === 'stack') {
        const childHtml = renderElement(child, allElements)
        return `      <div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%">\n    ${childHtml}\n      </div>`
      }
      return renderChildElement(child, allElements)
    })
    .filter(Boolean)
    .join('\n')

  return `    <div style="${innerStyleParts}">
${childrenHtml}
    </div>`
}

/** Render text element content without position wrapper (for use inside containers) */
function renderTextElementInner(el: TextElement): string {
  const trimmedContent = el.content?.trim() ?? ''
  const isBlock = trimmedContent.startsWith('<') || containsMarkdown(trimmedContent) || (el.content || '').includes('\n')
  const textStyle = buildTextInlineStyle(el.textStyle, isBlock)
  const borderStyle = buildBorderStyle(el.border)
  const paddingStyle = `padding: ${el.padding.top}px ${el.padding.right}px ${el.padding.bottom}px ${el.padding.left}px`
  const bgStyle = el.backgroundColor !== 'transparent' ? `background-color: ${el.backgroundColor}` : ''

  const vAlignMap: Record<string, string> = {
    top: 'flex-start',
    middle: 'center',
    bottom: 'flex-end',
  }
  const vAlign = `display: flex; flex-direction: column; justify-content: ${vAlignMap[el.textStyle.verticalAlign] ?? 'flex-start'}; width: 100%; height: 100%`

  const styleParts = [textStyle, borderStyle, paddingStyle, bgStyle, vAlign, 'overflow: hidden']
    .filter(Boolean)
    .join('; ')

  const markdownHtml = contentToHtml(el.content)
  return `<div style="${styleParts}">${markdownHtml}</div>`
}

/** Render image element content without position wrapper */
function renderImageElementInner(el: ImageElement): string {
  const borderStyle = buildBorderStyle(el.border)
  const bgStyle = el.backgroundColor !== 'transparent' ? `background-color: ${el.backgroundColor}` : ''
  const containerParts = ['width: 100%', 'height: 100%', 'overflow: hidden', borderStyle, bgStyle].filter(Boolean).join('; ')

  if (!el.src) {
    // No image source — don't render anything (no placeholder box)
    return ''
  }
  const altAttr = el.alt ? ` alt="${escapeAttr(el.alt)}"` : ' alt=""'
  const crop = el.cropRegion

  if (crop) {
    const imgW = `${(1 / crop.width) * 100}%`
    const imgH = `${(1 / crop.height) * 100}%`
    const imgLeft = `${(-crop.x / crop.width) * 100}%`
    const imgTop = `${(-crop.y / crop.height) * 100}%`
    const imgStyle = `position: absolute; width: ${imgW}; height: ${imgH}; left: ${imgLeft}; top: ${imgTop}; max-width: none; object-fit: fill`
    return `<div style="${containerParts}"><div style="width: 100%; height: 100%; position: relative; overflow: hidden"><img src="${escapeAttr(el.src)}"${altAttr} style="${imgStyle}" /></div></div>`
  }

  const imgStyle = `width: 100%; height: 100%; object-fit: ${el.objectFit}`
  return `<div style="${containerParts}"><img src="${escapeAttr(el.src)}"${altAttr} style="${imgStyle}" /></div>`
}

/** Render shape element content without position wrapper */
function renderShapeElementInner(el: ShapeElement): string {
  const borderStyle = buildBorderStyle(el.border)
  const fillStyle = el.fill !== 'transparent' ? `background-color: ${el.fill}` : ''
  let shadowStyle = ''
  if (el.shadow) {
    shadowStyle = `box-shadow: ${el.shadow.x}px ${el.shadow.y}px ${el.shadow.blur}px ${el.shadow.spread}px ${el.shadow.color}`
  }
  let shapeRadius = ''
  if (el.shapeKind === 'ellipse') {
    shapeRadius = 'border-radius: 50%'
  }
  const styleParts = ['width: 100%', 'height: 100%', fillStyle, borderStyle, shadowStyle, shapeRadius]
    .filter(Boolean)
    .join('; ')
  return `<div style="${styleParts}"></div>`
}

/** Render a group element: a positioned div containing absolutely-positioned children */
function renderGroupElement(el: GroupElement, allElements: SlideElement[]): string {
  const posStyle = buildBoxPositionStyle(el)
  const styleParts = [
    posStyle,
    el.clipContent ? 'overflow: hidden' : 'overflow: visible',
  ]

  const childrenHtml = el.childIds
    .map((childId) => {
      const child = allElements.find((e) => e.id === childId)
      if (!child || !child.visible) return ''
      // Children have positions relative to group origin
      const childPosStyle = buildBoxPositionStyle(child)
      let innerHtml = ''
      switch (child.type) {
        case 'text':
          innerHtml = renderTextElementInner(child as TextElement)
          break
        case 'markdown':
          innerHtml = renderTextElementInner(child as TextElement)
          break
        case 'image':
          innerHtml = renderImageElementInner(child as ImageElement)
          break
        case 'shape':
          innerHtml = renderShapeElementInner(child as ShapeElement)
          break
        case 'container': {
          const ctrWrapperStyle = [
            `position: absolute`,
            `left: ${child.rect.x}px`,
            `top: ${child.rect.y}px`,
            `width: ${child.rect.width}px`,
            `height: ${child.rect.height}px`,
            child.opacity < 1 ? `opacity: ${child.opacity}` : '',
            child.rotation !== 0 ? `transform: rotate(${child.rotation}deg)` : '',
          ].filter(Boolean).join('; ')
          return renderContainerChildContainer(child as ContainerElement, allElements, ctrWrapperStyle)
        }
        case 'group':
          return renderGroupElement(child as GroupElement, allElements)
        default:
          return ''
      }
      const wrapperStyle = [
        childPosStyle,
        `width: ${child.rect.width}px`,
        `height: ${child.rect.height}px`,
        child.opacity < 1 ? `opacity: ${child.opacity}` : '',
        child.rotation !== 0 ? `transform: rotate(${child.rotation}deg)` : '',
      ].filter(Boolean).join('; ')
      return `  <div style="${wrapperStyle}">\n${innerHtml}\n  </div>`
    })
    .filter(Boolean)
    .join('\n')

  return `<div style="${styleParts.filter(Boolean).join('; ')}">\n${childrenHtml}\n</div>`
}

function renderElement(el: SlideElement, allElements: SlideElement[]): string {
  if (!el.visible) return ''

  switch (el.type) {
    case 'text':
      return renderTextElement(el as TextElement)
    case 'markdown':
      return renderTextElement(el as TextElement)
    case 'image':
      return renderImageElement(el as ImageElement)
    case 'shape':
      return renderShapeElement(el as ShapeElement)
    case 'container':
      return renderContainerElement(el as ContainerElement, allElements)
    case 'group':
      return renderGroupElement(el as GroupElement, allElements)
    default:
      return ''
  }
}

// ─── HTML entity helpers ────────────────────────────────────────────────────

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function escapeAttr(str: string): string {
  return str.replace(/"/g, '&quot;').replace(/&/g, '&amp;')
}

// ─── Main export function ───────────────────────────────────────────────────

/**
 * Export a Slide to a self-contained HTML string.
 *
 * The output follows the same conventions as the existing presentation
 * templates: a root `<div>` sized to 1920×1080 with absolute-positioned
 * children, Google Fonts links inlined, and all styling via inline styles.
 */
export function exportSlideToHtml(slide: Slide): string {
  const googleFonts = collectGoogleFonts(slide)
  const fontsHtml = buildGoogleFontsLink(googleFonts)

  // Collect IDs that are children of a container or group (rendered inside the parent, not at root)
  const containerChildIds = new Set<string>()
  for (const el of slide.elements) {
    if (el.type === 'container') {
      for (const cid of (el as ContainerElement).childIds) {
        containerChildIds.add(cid)
      }
    }
    if (el.type === 'group') {
      for (const cid of (el as GroupElement).childIds) {
        containerChildIds.add(cid)
      }
    }
  }

  // Sort elements by zIndex so the DOM order matches visual stacking
  // Only render root-level elements (container children are rendered by their parent)
  const sortedElements = [...slide.elements]
    .filter((el) => el.visible && !containerChildIds.has(el.id))
    .sort((a, b) => a.zIndex - b.zIndex)

  const elementsHtml = sortedElements.map((el) => renderElement(el, slide.elements)).filter(Boolean).join('\n')

  const rootStyle = [
    `width: ${slide.width}px`,
    `height: ${slide.height}px`,
    `min-width: ${slide.width}px`,
    `min-height: ${slide.height}px`,
    `max-width: ${slide.width}px`,
    `max-height: ${slide.height}px`,
    `background-color: ${slide.backgroundColor}`,
    'position: relative',
    'overflow: hidden',
  ].join('; ')

  return `<div style="${rootStyle}">
  ${fontsHtml ? fontsHtml + '\n' : ''}
${elementsHtml}
</div>`
}

/**
 * Export a slide as a full standalone HTML document (useful for downloading).
 */
export function exportSlideToFullHtml(slide: Slide): string {
  const inner = exportSlideToHtml(slide)

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(slide.name)}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background: #1a1a1a;
    }
    /* Rich-text / Markdown styles for text elements */
    h1, h2, h3, h4, h5, h6 {
      font-family: inherit;
      color: inherit;
      line-height: inherit;
      letter-spacing: inherit;
      margin: 0;
    }
    h1 { font-size: 2em; font-weight: 700; }
    h2 { font-size: 1.5em; font-weight: 700; }
    h3 { font-size: 1.25em; font-weight: 600; }
    h4 { font-size: 1.1em; font-weight: 600; }
    h5 { font-size: 1em; font-weight: 600; }
    h6 { font-size: 0.9em; font-weight: 600; }
    p { margin: 0 0 0.5em 0; }
    p:last-child { margin-bottom: 0; }
    ul { margin: 0 0 0.5em 0; padding-left: 1.5em; list-style-type: disc; }
    ol { margin: 0 0 0.5em 0; padding-left: 1.5em; list-style-type: decimal; }
    li { margin: 0 0 0.35em 0; display: list-item; }
    li:last-child { margin-bottom: 0; }
    li p { margin: 0; }
    blockquote {
      margin: 0.2em 0;
      padding-left: 0.8em;
      border-left: 3px solid currentColor;
      opacity: 0.8;
    }
    code {
      font-family: 'IBM Plex Mono', monospace;
      font-size: 0.9em;
      background: rgba(0,0,0,0.06);
      padding: 0.1em 0.3em;
      border-radius: 3px;
    }
    pre {
      margin: 0.2em 0;
      padding: 0.5em;
      background: rgba(0,0,0,0.06);
      border-radius: 4px;
      overflow-x: auto;
    }
    pre code { background: none; padding: 0; }
    strong { font-weight: 700; }
    em { font-style: italic; }
    u { text-decoration: underline; }
    s { text-decoration: line-through; }
    a { color: inherit; text-decoration: underline; }
    hr {
      border: none;
      border-top: 1px solid currentColor;
      opacity: 0.3;
      margin: 0.4em 0;
    }
    table { border-collapse: collapse; margin: 0.4em 0; font-size: 0.9em; }
    th, td { border: 1px solid rgba(0,0,0,0.15); padding: 0.3em 0.6em; text-align: left; }
    th { font-weight: 600; }
    img { max-width: 100%; height: auto; }
  </style>
</head>
<body>
${inner}
</body>
</html>`
}

// ─── Rich-text CSS (shared between single-slide and multi-slide export) ─────
export const RICHTEXT_CSS = `
    h1, h2, h3, h4, h5, h6 {
      font-family: inherit;
      color: inherit;
      line-height: inherit;
      letter-spacing: inherit;
      margin: 0;
    }
    h1 { font-size: 2em; font-weight: 700; }
    h2 { font-size: 1.5em; font-weight: 700; }
    h3 { font-size: 1.25em; font-weight: 600; }
    h4 { font-size: 1.1em; font-weight: 600; }
    h5 { font-size: 1em; font-weight: 600; }
    h6 { font-size: 0.9em; font-weight: 600; }
    p { margin: 0 0 0.5em 0; }
    p:last-child { margin-bottom: 0; }
    ul { margin: 0 0 0.5em 0; padding-left: 1.5em; list-style-type: disc; }
    ol { margin: 0 0 0.5em 0; padding-left: 1.5em; list-style-type: decimal; }
    li { margin: 0 0 0.35em 0; display: list-item; }
    li:last-child { margin-bottom: 0; }
    li p { margin: 0; }
    blockquote {
      margin: 0.2em 0;
      padding-left: 0.8em;
      border-left: 3px solid currentColor;
      opacity: 0.8;
    }
    code {
      font-family: 'IBM Plex Mono', monospace;
      font-size: 0.9em;
      background: rgba(0,0,0,0.06);
      padding: 0.1em 0.3em;
      border-radius: 3px;
    }
    pre {
      margin: 0.2em 0;
      padding: 0.5em;
      background: rgba(0,0,0,0.06);
      border-radius: 4px;
      overflow-x: auto;
    }
    pre code { background: none; padding: 0; }
    strong { font-weight: 700; }
    em { font-style: italic; }
    u { text-decoration: underline; }
    s { text-decoration: line-through; }
    a { color: inherit; text-decoration: underline; }
    hr {
      border: none;
      border-top: 1px solid currentColor;
      opacity: 0.3;
      margin: 0.4em 0;
    }
    table { border-collapse: collapse; margin: 0.4em 0; font-size: 0.9em; }
    th, td { border: 1px solid rgba(0,0,0,0.15); padding: 0.3em 0.6em; text-align: left; }
    th { font-weight: 600; }
    img { max-width: 100%; height: auto; }
`

/**
 * Export multiple slides as a single print-ready HTML document.
 *
 * Uses CSS @page + page-break rules so the browser's native "Print → Save
 * as PDF" produces a pixel-perfect multi-page PDF at the exact slide
 * dimensions.  Each slide is rendered as a full-page section.
 *
 * @param slides        Array of Slide objects from the editor store
 * @param imageCache    Optional map of original image URL → data-URI for
 *                      embedding images inline (avoids CORS / network issues
 *                      when printing).
 * @param title         Document title shown in the browser tab / print header
 */
export function exportSlidesToPrintHtml(
  slides: Slide[],
  imageCache?: Map<string, string>,
  title = 'Presentation',
): string {
  if (slides.length === 0) return ''

  const slideWidth = slides[0].width
  const slideHeight = slides[0].height

  // Collect Google Fonts across ALL slides
  const allFamilies = new Set<string>()
  for (const slide of slides) {
    for (const family of collectGoogleFonts(slide)) {
      allFamilies.add(family)
    }
  }
  const fontsHtml = buildGoogleFontsLink(Array.from(allFamilies))

  // Build the HTML for each slide, optionally replacing image srcs
  const slidesHtml = slides
    .map((slide, idx) => {
      let slideHtml = exportSlideToHtml(slide)

      // Replace image URLs with cached data URIs
      if (imageCache && imageCache.size > 0) {
        for (const [originalSrc, dataUri] of imageCache) {
          slideHtml = slideHtml.split(originalSrc).join(dataUri)
          // Also replace HTML-escaped version of the URL
          const escapedSrc = originalSrc.replace(/&/g, '&amp;').replace(/"/g, '&quot;')
          if (escapedSrc !== originalSrc) {
            slideHtml = slideHtml.split(escapedSrc).join(dataUri)
          }
        }
      }

      // page-break-before on every slide after the first
      const pageBreak = idx > 0 ? 'page-break-before: always; ' : ''

      return `<div class="slide-page" style="${pageBreak}width: ${slideWidth}px; height: ${slideHeight}px; position: relative; overflow: hidden;">
${slideHtml}
</div>`
    })
    .join('\n')

  // Convert px to mm for the @page size (CSS @page uses mm/in/cm, not px).
  // Standard conversion: 1px = 0.264583mm at 96 DPI.
  const widthMm = (slideWidth * 25.4) / 96
  const heightMm = (slideHeight * 25.4) / 96

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(title)}</title>
  ${fontsHtml}
  <style>
    /* ── Reset ─────────────────────────────────────────────────── */
    * { margin: 0; padding: 0; box-sizing: border-box; }

    /* ── Print page size — matches the slide dimensions exactly ── */
    @page {
      size: ${widthMm.toFixed(2)}mm ${heightMm.toFixed(2)}mm;
      margin: 0;
    }

    /* ── Screen preview: center slides with a gap ─────────────── */
    @media screen {
      html { background: #1a1a1a; }
      body {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 24px;
        padding: 24px;
      }
      .slide-page {
        box-shadow: 0 4px 24px rgba(0,0,0,0.5);
        flex-shrink: 0;
      }
    }

    /* ── Print: each slide fills one page exactly ─────────────── */
    @media print {
      html, body { width: ${slideWidth}px; height: auto; }
      body { display: block; padding: 0; gap: 0; }
      .slide-page {
        page-break-after: always;
        page-break-inside: avoid;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        color-adjust: exact;
      }
      .slide-page:last-child {
        page-break-after: auto;
      }
    }

    /* ── Rich-text / Markdown styles ──────────────────────────── */
    ${RICHTEXT_CSS}
  </style>
</head>
<body>
${slidesHtml}
</body>
</html>`
}
